import { useCallback, useMemo, useState } from 'react'
import {
  CheckIcon,
  ChildrenIcon,
  DataSurface,
  InfoIcon,
  Note,
  Pagination,
  PlayIcon,
  SectionHead,
  StatCard,
  StatGrid,
  TableCard,
  Td,
  Thead,
  Tr,
  TrendUpIcon,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import {
  getSchoolGameLog,
  type GameLogRow,
  type SchoolGameLog,
} from '../../../shared/api/school'
import { getClasses, type ClassSummary } from '../../../shared/api/classes'
import type { StaffAuth } from '../../../shared/api/teaching'
import { GAME_ART_BY_ID, type GameArt } from '../../../shared/ui/gameArt'
import type { Lang } from './copy'
import { describeAdminError } from './adminCopy'
import { accuracyOf, durationMinutes, gameLogToCsv, pageCount, rangeLabel } from './gameLogModel'
import {
  ActiveFilters,
  FilterAction,
  FilterDate,
  FilterField,
  FilterPresets,
  FilterSelect,
  downloadCsv,
  type FilterChip,
} from './FilterKit'

/**
 * The play log — every sitting, with the child, the game and the teacher on it.
 *
 * 🔴 THE SCREEN E1's GAME CARDS COULD NOT BE. A card says *"23 of 33 children
 * have played Kalqy World"*, which answers whether a game is being used and is
 * silent on the question a school is actually asked at the gate: *"has MY child
 * played, and when?"* Answering that meant opening E7, finding the child, and
 * reading one report at a time — for a question about a game, not a child.
 *
 * 🔴 A ROW IS ONE SITTING OF ONE GAME, AND THE SCREEN SAYS SO IN WORDS. The
 * server's `basis` string is printed under the table rather than paraphrased
 * here, because the grain is genuinely surprising: `Session` carries no game
 * column, so a sitting in which a child opened two games is two lines. A reader
 * who assumed one line per sitting would count the same sitting twice.
 *
 * ⚠️ THE FILTERS ARE THE SERVER'S, NOT THE BROWSER'S. Narrowing a page that has
 * already been taken shows a short page under a total that promised a full one —
 * so every control here refetches, and `total` always describes what was
 * filtered.
 */
export interface SchoolGamesScreenProps {
  lang?: Lang
  auth?: StaffAuth
  /**
   * The game to open pre-filtered, from a card on E1.
   *
   * ⚠️ IT SEEDS THE FILTER RATHER THAN LOCKING IT. Arriving from "Bridge
   * Patterns" should show Bridge Patterns and still let a reader widen to all
   * games without going back — a filter that cannot be cleared is a dead end.
   */
  game?: string
}

/** The window presets. The server clamps anything wider — `MAX_RANGE_DAYS`. */
const WINDOWS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
] as const

const WINDOW_OPTIONS = WINDOWS.map((w) => ({ value: w.days as number, label: w.label }))

const PAGE_SIZE = 25

/** `sky-quest` → `Sky Quest`, and an unknown id stays itself rather than blank. */
/*
 * ⚠️ THE MAP IS KEYED BY `GameId` AND A LOG ROW'S `game` IS A PLAIN STRING,
 * DELIBERATELY. `Event.game` is a string in the schema *"so the versioned
 * content library (F4) can add games without a migration"* — which means this
 * screen can be handed a game it has no art for, and must draw the id rather
 * than a blank cell or a crash.
 */
const artOf = (id: string): GameArt | undefined =>
  (GAME_ART_BY_ID as Record<string, GameArt | undefined>)[id]

const titleOf = (id: string, lang: Lang): string => artOf(id)?.title[lang] ?? id

/** ISO day, N days back from `now`, inclusive of today. */
const dayBefore = (now: Date, days: number): string =>
  new Date(now.getTime() - (days - 1) * 86_400_000).toISOString().slice(0, 10)

const isoDay = (d: Date): string => d.toISOString().slice(0, 10)

export function SchoolGamesScreen({ lang = 'en', auth, game }: SchoolGamesScreenProps) {
  const now = useMemo(() => new Date(), [])

  const [days, setDays] = useState<number>(30)
  const [from, setFrom] = useState<string>(() => dayBefore(now, 30))
  const [to, setTo] = useState<string>(() => isoDay(now))
  const [gameId, setGameId] = useState<string>(game ?? '')
  const [classId, setClassId] = useState<string>('')
  const [teacherId, setTeacherId] = useState<string>('')
  const [page, setPage] = useState(1)

  /*
   * ⚠️ THE CLASS LIST IS ITS OWN READ, `SchoolStudentsScreen`'s REASON EXACTLY.
   * Deriving it from the rows on screen would drop every class that has not
   * played — and a class with no sittings is the one an admin is looking for.
   */
  const classList = useResource<ClassSummary[]>(
    (signal) => getClasses({ ...auth, signal }),
    ['school-classes'],
  )

  const query = useMemo(
    () => ({
      from,
      to,
      game: gameId === '' ? undefined : gameId,
      classId: classId === '' ? undefined : classId,
      teacherId: teacherId === '' ? undefined : teacherId,
      page,
      pageSize: PAGE_SIZE,
    }),
    [from, to, gameId, classId, teacherId, page],
  )

  /*
   * 🔴 EVERY FILTER IS IN THE CACHE KEY. `useResource` refetches when the key
   * changes, so a key missing `teacherId` would leave the previous teacher's
   * rows on screen under the new teacher's name — the stale-read bug that looks
   * like a filter which does nothing.
   */
  const resource = useResource<SchoolGameLog>(
    (signal) => getSchoolGameLog(query, { ...auth, signal }),
    [
      'school-game-log',
      from,
      to,
      gameId,
      classId,
      teacherId,
      String(page),
    ],
    { isEmpty: (log) => log.rows.length === 0 },
  )

  /* Changing what is being asked must return to page 1, or a reader lands on
     page 9 of a filter with two pages and reads it as "no results". */
  const refilter = useCallback((apply: () => void) => {
    apply()
    setPage(1)
  }, [])

  const applyWindow = useCallback(
    (n: number) => {
      refilter(() => {
        setDays(n)
        setFrom(dayBefore(now, n))
        setTo(isoDay(now))
      })
    },
    [now, refilter],
  )

  const log = resource.data
  const rows = useMemo(() => log?.rows ?? [], [log])

  const download = useCallback(() => {
    downloadCsv(gameLogToCsv(rows, (id) => titleOf(id, lang)), `kalqy-play-log-${from}-to-${to}.csv`)
  }, [rows, lang, from, to])

  /* The period is always set, so it is not a chip; the three "who" filters are. */
  const chips = useMemo<FilterChip[]>(() => {
    const out: FilterChip[] = []
    if (gameId !== '')
      out.push({ key: 'game', label: titleOf(gameId, lang), onRemove: () => { refilter(() => { setGameId('') }) } })
    if (classId !== '')
      out.push({
        key: 'class',
        label: classList.data?.find((c) => c.id === classId)?.name ?? 'Class',
        onRemove: () => { refilter(() => { setClassId('') }) },
      })
    if (teacherId !== '')
      out.push({
        key: 'teacher',
        label: log?.teachers.find((t) => t.teacherId === teacherId)?.name ?? 'Teacher',
        onRemove: () => { refilter(() => { setTeacherId('') }) },
      })
    return out
  }, [gameId, classId, teacherId, lang, classList.data, log, refilter])

  const clearAll = useCallback(() => {
    refilter(() => {
      setGameId('')
      setClassId('')
      setTeacherId('')
    })
  }, [refilter])

  /*
   * 🔴 THE HEADLINE FIGURES DESCRIBE THIS PAGE AND SAY SO. Summing rounds across
   * 25 sittings and calling it "rounds this term" would be a school-wide claim
   * computed from one page — the contradiction `design-system.md` §8 forbids,
   * and the reader would be right to stop trusting the table under it. The word
   * "on this page" is what makes each of these honest.
   */
  const totals = useMemo(() => {
    let rounds = 0
    let correct = 0
    let wrong = 0
    const children = new Set<string>()
    for (const r of rows) {
      rounds += r.rounds
      correct += r.correct
      wrong += r.wrong
      children.add(r.playerId)
    }
    const answered = correct + wrong
    return {
      rounds,
      children: children.size,
      accuracy: answered === 0 ? null : Math.round((correct / answered) * 100),
    }
  }, [rows])

  const totalPages = pageCount(log?.total ?? 0, log?.pageSize ?? PAGE_SIZE)

  return (
    <section className="flex flex-col gap-6 font-dash text-dash-sm text-ink">
      <StatGrid>
        <StatCard
          label="Sittings found"
          value={String(log?.total ?? 0)}
          caption="Across the whole filter, not just this page"
          icon={<PlayIcon size={20} />}
        />
        <StatCard
          label="Children on this page"
          value={String(totals.children)}
          caption="Distinct children in the rows below"
          icon={<ChildrenIcon size={20} />}
          tone="indigo"
        />
        <StatCard
          label="Rounds on this page"
          value={String(totals.rounds)}
          caption="Levels finished, not levels opened"
          icon={<TrendUpIcon size={20} />}
          tone="leaf"
        />
        {/* 🔴 THE DENOMINATOR IS IN THE CAPTION, which is what OD-39/OD-72
            actually require of a percentage — never a bare share. */}
        <StatCard
          label="Accuracy on this page"
          value={totals.accuracy === null ? '—' : `${String(totals.accuracy)}%`}
          /* ⚠️ THREE STATES, NOT TWO. "These games record no answers" is true of
             a page of Kalqy World and a lie about a page with nothing on it at
             all — which is what an over-narrow filter produces, and the moment a
             reader most needs to be told it is the filter rather than the data. */
          caption={
            rows.length === 0
              ? 'Nothing matches these filters yet'
              : totals.accuracy === null
                ? 'These games record no answers'
                : 'Correct out of answered'
          }
          icon={<CheckIcon size={20} />}
          tone="teal"
        />
      </StatGrid>

      <SectionHead hint="Narrow the log, then export what is left.">Filters</SectionHead>

      <div className="rounded-3xl border border-line bg-surface p-4 shadow-card sm:p-5">
        {/* Two rows on purpose: who (game, class, teacher) spans the card, then
            when (dates and quick windows) sits beside the export. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <FilterField label="Game">
            <FilterSelect
              aria-label="Filter the play log by game"
              value={gameId}
              active={gameId !== ''}
              onChange={(e) => { refilter(() => { setGameId(e.target.value) }) }}
            >
              <option value="">All games</option>
              {Object.values(GAME_ART_BY_ID).map((g) => (
                <option key={g.id} value={g.id}>{g.title[lang]}</option>
              ))}
            </FilterSelect>
          </FilterField>

          <FilterField label="Class">
            <FilterSelect
              aria-label="Filter the play log by class"
              value={classId}
              active={classId !== ''}
              onChange={(e) => { refilter(() => { setClassId(e.target.value) }) }}
            >
              <option value="">All classes</option>
              {(classList.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </FilterSelect>
          </FilterField>

          {/* ⚠️ THE TEACHERS COME FROM THE ANSWER, NOT FROM THE STAFF LIST. The
              server returns the teachers present on this page, so every option
              narrows to something rather than offering a name with no sittings. */}
          <FilterField label="Teacher">
            <FilterSelect
              aria-label="Filter the play log by teacher"
              value={teacherId}
              active={teacherId !== ''}
              onChange={(e) => { refilter(() => { setTeacherId(e.target.value) }) }}
            >
              <option value="">All teachers</option>
              {(log?.teachers ?? []).map((t) => (
                <option key={t.teacherId} value={t.teacherId}>{t.name}</option>
              ))}
            </FilterSelect>
          </FilterField>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-line pt-4">
          <FilterField label="Period">
            <FilterPresets
              label="Quick period"
              options={WINDOW_OPTIONS}
              value={days === 0 ? null : days}
              onChange={applyWindow}
            />
          </FilterField>

          <FilterField label="From">
            <FilterDate
              aria-label="Show sittings from this date"
              value={from}
              max={to}
              onChange={(e) => { refilter(() => { setDays(0); setFrom(e.target.value) }) }}
            />
          </FilterField>

          <FilterField label="To">
            <FilterDate
              aria-label="Show sittings up to this date"
              value={to}
              min={from}
              max={isoDay(now)}
              onChange={(e) => { refilter(() => { setDays(0); setTo(e.target.value) }) }}
            />
          </FilterField>

          <div className="ml-auto">
            <FilterAction onClick={download} disabled={rows.length === 0}>
              Export CSV
            </FilterAction>
          </div>
        </div>

        {chips.length > 0 && (
          <div className="mt-4">
            <ActiveFilters chips={chips} onClearAll={clearAll} summary="Filtered by" />
          </div>
        )}
      </div>

      <SectionHead hint={rangeLabel({ total: log?.total ?? 0, page, pageSize: log?.pageSize ?? PAGE_SIZE })}>
        The play log
      </SectionHead>

      <DataSurface
        state={resource.state}
        skeletonRows={8}
        emptyMessage="No sittings match these filters. Widen the dates, or clear a filter."
        errorMessage={describeAdminError(resource.error, lang)}
        errorRetryLabel="Try again"
        onRetry={resource.retry}
      >
        {/* ⚠️ THE LABEL IS THE SCROLL REGION'S ACCESSIBLE NAME, not decoration
            — `TableCard` makes the region focusable so a keyboard reader can
            scroll it sideways, and an unlabelled focus stop is announced as
            "group". Nine columns will scroll on any laptop. */}
        <TableCard label="Play log">
          <table className="w-full border-collapse text-left">
            <Thead
              headers={['When', 'Student', 'Class', 'Game', 'Teacher', 'Rounds', 'Accuracy', 'Level', 'Minutes']}
            />
            <tbody>
              {rows.map((row) => (
                <LogRow key={`${row.sessionId}-${row.game}`} row={row} lang={lang} />
              ))}
            </tbody>
          </table>
        </TableCard>
      </DataSurface>

      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={log?.total ?? 0}
        />
      )}

      {/* 🔴 THE SERVER'S OWN SENTENCE, PRINTED RATHER THAN PARAPHRASED. The grain
          is surprising — a sitting that held two games is two lines — and a
          reader who assumed one line per sitting would double-count it. */}
      {log !== null && log !== undefined && (
        <Note tone="neutral" icon={<InfoIcon size={16} />}>
          {log.basis} Showing {log.windowFrom} to {log.windowTo}.
        </Note>
      )}
    </section>
  )
}

/** One sitting of one game. */
function LogRow({ row, lang }: { row: GameLogRow; lang: Lang }) {
  const played = new Date(row.playedAt)
  const accuracy = accuracyOf(row)
  const minutes = durationMinutes(row)
  const art = artOf(row.game)

  return (
    <Tr>
      <Td>
        <span className="tabular block font-bold text-ink">
          {played.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </span>
        <span className="tabular block text-dash-xs text-muted">
          {played.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </Td>
      <Td>
        <span className="block font-bold text-ink">{row.studentName}</span>
        {row.rollNumber !== null && (
          <span className="tabular block text-dash-xs text-muted">Roll {row.rollNumber}</span>
        )}
      </Td>
      <Td>{row.className ?? <span className="text-muted">No class</span>}</Td>
      <Td>
        <span className="flex items-center gap-2 font-bold" style={art ? { color: art.tint } : undefined}>
          {art && (
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: art.tint }}
            />
          )}
          {titleOf(row.game, lang)}
        </span>
      </Td>
      {/* ⚠️ A NULL TEACHER IS A FACT WITH A NAME. `Session.teacherId` is null for
          home and solo play; an empty cell would read as data that went missing. */}
      <Td>{row.teacherName ?? <span className="text-muted">Outside class</span>}</Td>
      <Td><span className="tabular font-bold text-ink">{row.rounds}</span></Td>
      <Td>
        {accuracy === null ? (
          /* ⚠️ Kalqy World records gestures, not answers — "0%" would say the
             child got everything wrong when the game never asked. */
          <span className="text-muted" title="This game records no answers">—</span>
        ) : (
          <span className="tabular">
            {accuracy}%{' '}
            <span className="text-dash-xs text-muted">({row.correct}/{row.correct + row.wrong})</span>
          </span>
        )}
      </Td>
      <Td>
        {row.highestLevel === null ? <span className="text-muted">—</span> : <span className="tabular">L{row.highestLevel}</span>}
      </Td>
      <Td>
        {minutes === null ? (
          <span className="text-muted" title="This sitting was never closed">—</span>
        ) : (
          <span className="tabular">{minutes}</span>
        )}
      </Td>
    </Tr>
  )
}
